import { Box, IconButton, MenuProps, Stack, TablePagination, Typography, useTheme } from '@mui/material';
import React from 'react';

import { useTranslation } from '../../utils/translations';
import { colors } from '../theme/theme';
import { breakpoints } from '../theme/Typography';

const ALL_ROWS_VALUE = -1;

interface CustomPaginationProps {
    totalCount: number;
    rowsPerPage: number;
    currentPage: number;
    onPageChange: (newPage: number, rowsPerPage: number) => void;
    onRowsPerPageChange?: (rowsPerPage: number) => void;
    sx?: Record<string, unknown>;
    serverSide?: boolean;
    sortedRowsLength?: number;
    isSchedulePaymentNote?: boolean;
}
interface PaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

export const CustomPagination: React.FC<CustomPaginationProps> = ({
    totalCount,
    rowsPerPage,
    currentPage,
    onPageChange,
    onRowsPerPageChange,
    sx,
    serverSide = false,
    sortedRowsLength = 0,
    isSchedulePaymentNote = false,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const menuProps: Partial<MenuProps> = React.useMemo(
        () => ({
            PaperProps: {
                sx: {
                    border: `1px solid ${colors.greyPreset[20]}`,
                    '& .MuiMenuItem-root': {
                        ...theme.typography.body2,
                    },
                },
            },
        }),
        [theme],
    );
    const rowsPerPageLabelId = React.useId();
    const rowsPerPageSelectId = React.useId();

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        onPageChange(newPage, rowsPerPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        if (onRowsPerPageChange) {
            onRowsPerPageChange(newRowsPerPage);
        } else {
            onPageChange(0, newRowsPerPage);
        }
    };
    const ArrowDropDownIcon = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((props, ref) => (
        <Box
            ref={ref}
            component="span"
            sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'end', mr: 0.5 }}
            {...props}
        >
            <span className="icon-arrow-drop-down" style={{ fontSize: 16, color: colors.fullBlack }} />
        </Box>
    ));
    ArrowDropDownIcon.displayName = 'ArrowDropDownIcon';

    return (
        <Stack
            direction={'row'}
            justifyContent={isSchedulePaymentNote ? 'space-between' : 'flex-end'}
            pt={1.5}
            sx={{
                py: '13px',
                '& .MuiTablePagination-root': {
                    border: 'none',
                },
                ...sx,
            }}
        >
            {isSchedulePaymentNote && (
                <Stack direction={'row'} gap={0.5} px={3}>
                    <Typography variant="body2" color="textPrimary" fontWeight={600}>
                        {t('payments.paymentMethod.pendingPaymentNoteLabel')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t('payments.paymentMethod.pendingPaymentNoteMessage')}
                    </Typography>
                </Stack>
            )}

            <TablePagination
                component="div"
                ActionsComponent={PaginationActions}
                count={serverSide ? totalCount : sortedRowsLength}
                page={rowsPerPage === ALL_ROWS_VALUE || rowsPerPage === -1 ? 0 : currentPage}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage === ALL_ROWS_VALUE || rowsPerPage === -1 ? ALL_ROWS_VALUE : rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                slotProps={{
                    selectLabel: { id: rowsPerPageLabelId },
                }}
                labelDisplayedRows={({ from, to, count }) => (
                    <>
                        {from} <span style={{ color: colors.fullBlack, margin: '0px 5px' }}>-</span>
                        {to} <span style={{ color: colors.fullBlack, margin: '0px 5px' }}> of </span>
                        {count !== -1 ? count : `more than ${to}`}
                    </>
                )}
                SelectProps={{
                    MenuProps: menuProps,
                    IconComponent: ArrowDropDownIcon,
                    labelId: rowsPerPageLabelId,
                    inputProps: {
                        id: rowsPerPageSelectId,
                        'aria-labelledby': rowsPerPageLabelId,
                    },
                    renderValue: (selected: unknown) => {
                        const numValue = typeof selected === 'string' ? parseInt(selected, 10) : selected;
                        if (numValue === ALL_ROWS_VALUE || numValue === -1) {
                            return 'All';
                        }
                        return String(selected || '');
                    },
                }}
                sx={{
                    '& .MuiInputBase-root': {
                        border: `1px solid ${colors.greyPreset[20]}`,
                        borderRadius: 1,
                        padding: '4px 0px 4px 6px',
                        [breakpoints.down('sm')]: {
                            padding: '6px 0px 6px 6px',
                        },
                        '& .MuiSelect-select': {
                            padding: '0px 18px 0px 0px !important',
                        },
                        '& .MuiSelect-select.MuiSelect-select': {
                            '&:empty::before': {
                                content: '""',
                            },
                        },
                    },
                    '& .MuiTablePagination-toolbar': {
                        paddingLeft: 3,
                        paddingRight: 3,
                        minHeight: 'auto !important',
                        margin: 0,
                    },
                    '& .MuiTablePagination-selectLabel': {
                        ...theme.typography.body2,
                        color: theme.palette.text.primary,
                        fontWeight: 600,
                        margin: 0,
                    },
                    '& .MuiTablePagination-displayedRows': {
                        ...theme.typography.body2,
                        color: colors.greyPreset[80],
                        fontWeight: 600,
                        margin: 0,
                    },
                    '& .MuiTablePagination-select': {
                        ...theme.typography.body2,
                        color: colors.greyPreset[80],
                        fontWeight: 600,
                        marginRight: '10px',
                    },
                    '& .MuiIconButton-root': {
                        color: colors.fullBlack,
                        padding: 0,
                        '&:hover': {
                            backgroundColor: colors.greyPreset[90],
                        },
                        '&.Mui-disabled': {
                            color: theme.palette.text.disabled,
                        },
                        ...((rowsPerPage === ALL_ROWS_VALUE || rowsPerPage === -1) && {
                            '&:not(.Mui-disabled)': {
                                pointerEvents: 'none',
                                opacity: 0.5,
                            },
                        }),
                    },
                }}
            />
        </Stack>
    );
};

const PaginationActions = ({ count, page, rowsPerPage, onPageChange }: PaginationActionsProps) => {
    const handleBack = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    const handleNext = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    const isLastPage = rowsPerPage !== -1 && page >= Math.ceil(count / rowsPerPage) - 1;
    return (
        <Stack
            direction={'row'}
            ml={1}
            sx={{
                gap: 0.8,
                '& .MuiButtonBase-root': {
                    border: `1px solid ${colors.greyPreset[20]}`,
                    borderRadius: 1,
                    padding: '6px',
                },
            }}
        >
            <IconButton onClick={handleBack} disabled={page === 0} aria-label="Go to previous page">
                <span className="icon-arrow-drop-up" style={{ transform: 'rotate(-90deg)', fontSize: 16 }} />
            </IconButton>

            <IconButton onClick={handleNext} disabled={isLastPage} aria-label="Go to next page">
                <span className="icon-arrow-drop-down" style={{ transform: 'rotate(-90deg)', fontSize: 16 }} />
            </IconButton>
        </Stack>
    );
};
